/**
 * FLESH SHELLS — the organic skin geometry for the mega_chibre realism pass
 * (mapExtras/mega_chibre.ts). Pure three.js geometry: no DOM, no textures, so
 * `agent/bench-extras-chibre.mjs` can build every shell offline and check it
 * against the real shared collider boxes.
 *
 * A shell is the Minkowski sum of the collider box with a small ellipsoid
 * (rounded edges that never cut INTO the box, so the plain box it replaces is
 * fully hidden), then pushed outward by a "pillow" bulge that is zero at the
 * edges and grows toward the middle of each face, modulated by smooth noise.
 * The result reads as soft, swollen tissue hugging the box, while the extra
 * thickness is bounded exactly:
 *
 *   side faces  ≤ R_SIDE + BULGE_SIDE   (≤ SIDE_MAX = 0.26 m, the wall rule)
 *   top faces   ≤ R_TOP  + BULGE_TOP + stack offset (≤ TOP_MAX — feet sink a
 *                 few cm into the skin, never float above it)
 *
 * Veins are tapered tubes laid on the SAME analytic surface (`surfacePoint`),
 * so they sit on the skin instead of floating or burying.
 */
import * as THREE from "three";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { MapBox } from "@neon-purge/shared";

// contract numbers (the bench quotes these)
export const R_SIDE = 0.12;
export const R_TOP = 0.05;
export const R_BOTTOM = 0.12;
export const BULGE_SIDE = 0.12;
export const BULGE_TOP = 0.035;
/** per-slab stacking step so overlapping coplanar tops never z-fight */
export const STACK_STEP = 0.012;
export const SIDE_MAX = 0.26;
export const TOP_MAX = 0.16;
export const VEIN_R_MAX = 0.11;

// ---------------------------------------------------------------------------
// deterministic smooth noise (value noise, fbm) — client-only, no Math.random
function hash3(x: number, y: number, z: number, s: number): number {
  let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ Math.imul(z | 0, 1274126177) ^ Math.imul(s | 0, 1442695041);
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
const fade = (t: number): number => t * t * (3 - 2 * t);
export function vnoise(x: number, y: number, z: number, s = 0): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const xf = fade(x - xi), yf = fade(y - yi), zf = fade(z - zi);
  const l = (a: number, b: number, t: number): number => a + (b - a) * t;
  const c = (dx: number, dy: number, dz: number): number => hash3(xi + dx, yi + dy, zi + dz, s);
  return l(
    l(l(c(0, 0, 0), c(1, 0, 0), xf), l(c(0, 1, 0), c(1, 1, 0), xf), yf),
    l(l(c(0, 0, 1), c(1, 0, 1), xf), l(c(0, 1, 1), c(1, 1, 1), xf), yf),
    zf,
  );
}
/** fbm in [0,1] */
export function fbm(x: number, y: number, z: number, s = 0): number {
  return (vnoise(x, y, z, s) * 0.55 + vnoise(x * 2.1, y * 2.1, z * 2.1, s + 7) * 0.3 + vnoise(x * 4.3, y * 4.3, z * 4.3, s + 13) * 0.15);
}
/** seeded PRNG for layout (mulberry32) */
export function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const smooth = (e0: number, e1: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

export interface ShellSpec {
  box: MapBox;
  /** stacking rank: tops of overlapping slabs get rank·STACK_STEP extra */
  rank?: number;
  /** pillow strength multiplier (0..1) */
  bulge?: number;
  /** noise seed */
  seed?: number;
  /** target grid spacing in metres */
  seg: number;
  /** linear RGB-ish tint 0..1 (sRGB hex is converted by the caller) */
  color: THREE.Color;
  /** colour variation amount */
  mottle?: number;
}

/** Bulge scale for a direction: full on sides/bottom, tiny on tops (standable). */
function bulgeFor(dy: number, k: number): number {
  const t = smooth(0.55, 0.95, dy);
  return k * (BULGE_SIDE + (BULGE_TOP - BULGE_SIDE) * t);
}

/**
 * The analytic outer surface of a shell at a point on one FLAT face of the box
 * (`axis` 0/1/2, `sign` ±1, tangent coords `u`,`v` in world units along the
 * two other axes, in increasing axis order). Shared by the shell vertices and
 * the vein tubes, so veins lie exactly on the skin.
 */
export function surfacePoint(spec: ShellSpec, axis: number, sign: 1 | -1, u: number, v: number, out: THREE.Vector3): THREE.Vector3 {
  const b = spec.box;
  const h = [b.s[0] / 2, b.s[1] / 2, b.s[2] / 2];
  const ta = axis === 0 ? [1, 2] : axis === 1 ? [0, 2] : [0, 1];
  const c = [0, 0, 0];
  c[axis] = sign * h[axis];
  c[ta[0]] = Math.max(-h[ta[0]], Math.min(h[ta[0]], u - b.p[ta[0]]));
  c[ta[1]] = Math.max(-h[ta[1]], Math.min(h[ta[1]], v - b.p[ta[1]]));
  const dir = [0, 0, 0];
  dir[axis] = sign;
  return place(spec, h, c, dir, out);
}

function radiusFor(j: number, sgn: number): number {
  if (j === 1) return sgn > 0 ? R_TOP : R_BOTTOM;
  return R_SIDE;
}

/** c = clamped point on the box (local), dir = unit outward direction. */
function place(spec: ShellSpec, h: number[], c: number[], dir: number[], out: THREE.Vector3): THREE.Vector3 {
  const b = spec.box;
  // rounded offset (ellipsoid radii per axis/sign)
  const off = [0, 0, 0];
  for (let j = 0; j < 3; j++) off[j] = dir[j] * radiusFor(j, dir[j]);
  // pillow weight: product of edge falloffs over every axis except the
  // dominant one (continuous across the rounded edges, where it is 0)
  let dom = 0;
  if (Math.abs(dir[1]) >= Math.abs(dir[dom])) dom = 1;
  if (Math.abs(dir[2]) > Math.abs(dir[dom])) dom = 2;
  let w = 1;
  for (let j = 0; j < 3; j++) {
    if (j === dom) continue;
    const F = Math.min(1.4, Math.max(0.05, h[j] * 0.8));
    w *= smooth(0, F, h[j] - Math.abs(c[j]));
  }
  const wx = b.p[0] + c[0], wy = b.p[1] + c[1], wz = b.p[2] + c[2];
  const n = fbm(wx * 0.45, wy * 0.45, wz * 0.45, spec.seed ?? 1);
  const amp = bulgeFor(dir[1], spec.bulge ?? 1) * w * (0.35 + 0.65 * n);
  const stack = dir[1] > 0.5 ? (spec.rank ?? 0) * STACK_STEP * smooth(0.5, 0.95, dir[1]) : 0;
  return out.set(
    wx + off[0] + dir[0] * amp,
    wy + off[1] + dir[1] * (amp + stack),
    wz + off[2] + dir[2] * amp,
  );
}

/** axis sample list for half-extent h with rounding band radii rn/rp */
function axisList(h: number, rn: number, rp: number, seg: number): number[] {
  const n = Math.max(1, Math.ceil((2 * h) / seg));
  const out = [-h - rn, -h - rn * 0.41];
  for (let i = 0; i <= n; i++) out.push(-h + (2 * h * i) / n);
  out.push(h + rp * 0.41, h + rp);
  return out;
}

const _v = new THREE.Vector3();
/** Build one shell (indexed, merged vertices, with normals, uv, color). */
export function shellGeometry(spec: ShellSpec, uvTile: number): THREE.BufferGeometry {
  const b = spec.box;
  const h = [b.s[0] / 2, b.s[1] / 2, b.s[2] / 2];
  const R = [[R_SIDE, R_SIDE], [R_BOTTOM, R_TOP], [R_SIDE, R_SIDE]];
  const L = [0, 1, 2].map((j) => axisList(h[j], R[j][0], R[j][1], spec.seg));
  const pos: number[] = [];
  const idx: number[] = [];
  // six faces of the (expanded) grid box
  for (let axis = 0; axis < 3; axis++) {
    const ta = axis === 0 ? [1, 2] : axis === 1 ? [0, 2] : [0, 1];
    for (const sign of [-1, 1]) {
      const fixed = sign < 0 ? L[axis][0] : L[axis][L[axis].length - 1];
      const A = L[ta[0]], B = L[ta[1]];
      const base = pos.length / 3;
      for (let j = 0; j < B.length; j++) for (let i = 0; i < A.length; i++) {
        const p = [0, 0, 0];
        p[axis] = fixed; p[ta[0]] = A[i]; p[ta[1]] = B[j];
        pos.push(p[0], p[1], p[2]);
      }
      const W = A.length;
      for (let j = 0; j < B.length - 1; j++) for (let i = 0; i < W - 1; i++) {
        const a = base + j * W + i, bb = a + 1, c = a + W, d = c + 1;
        // winding so the face points outward (cross(ta0, ta1) = ±axis)
        const flip = (axis === 1 ? -sign : sign) < 0;
        if (!flip) idx.push(a, bb, d, a, d, c); else idx.push(a, d, bb, a, c, d);
      }
    }
  }
  let g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g = mergeVertices(g, 1e-5);
  const P = g.getAttribute("position") as THREE.BufferAttribute;
  const c = [0, 0, 0], dir = [0, 0, 0];
  for (let k = 0; k < P.count; k++) {
    const p = [P.getX(k), P.getY(k), P.getZ(k)];
    let len = 0;
    for (let j = 0; j < 3; j++) {
      c[j] = Math.max(-h[j], Math.min(h[j], p[j]));
      const d = p[j] - c[j];
      const r = radiusFor(j, d);
      dir[j] = r > 0 ? d / r : 0;
      len += dir[j] * dir[j];
    }
    len = Math.sqrt(len) || 1;
    for (let j = 0; j < 3; j++) dir[j] /= len;
    place(spec, h, c, dir, _v);
    P.setXYZ(k, _v.x, _v.y, _v.z);
  }
  g.computeVertexNormals();
  boxProjectUV(g, uvTile);
  tintVertices(g, spec.color, spec.mottle ?? 1, spec.seed ?? 1, b.p[1] - h[1], b.s[1]);
  return g;
}

/** UVs by dominant normal axis, world anchored. */
export function boxProjectUV(g: THREE.BufferGeometry, tile: number): void {
  const P = g.getAttribute("position");
  const N = g.getAttribute("normal");
  const uv = new Float32Array(P.count * 2);
  for (let i = 0; i < P.count; i++) {
    const nx = Math.abs(N.getX(i)), ny = Math.abs(N.getY(i)), nz = Math.abs(N.getZ(i));
    let u: number, v: number;
    if (nx >= ny && nx >= nz) { u = P.getZ(i); v = P.getY(i); }
    else if (ny >= nz) { u = P.getX(i); v = P.getZ(i); }
    else { u = P.getX(i); v = P.getY(i); }
    uv[i * 2] = u / tile; uv[i * 2 + 1] = v / tile;
  }
  g.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
}

const _c = new THREE.Color();
const RED = new THREE.Color(0.62, 0.2, 0.2);
/** Skin tone + blotchy redness + darker toward the base of tall pieces (AO). */
export function tintVertices(g: THREE.BufferGeometry, base: THREE.Color, mottle: number, seed: number, y0: number, hy: number): void {
  const P = g.getAttribute("position");
  const col = new Float32Array(P.count * 3);
  for (let i = 0; i < P.count; i++) {
    const x = P.getX(i), y = P.getY(i), z = P.getZ(i);
    const m = fbm(x * 0.22, y * 0.22, z * 0.22, seed + 31);
    const r = smooth(0.45, 0.8, fbm(x * 0.6 + 3, y * 0.6, z * 0.6, seed + 57));
    _c.copy(base).multiplyScalar(0.86 + 0.24 * m * mottle);
    _c.lerp(RED, 0.18 * r * mottle);
    // contact darkening: the lowest 0.5 m of anything taller than 0.8 m
    if (hy > 0.8) _c.multiplyScalar(0.78 + 0.22 * smooth(0, 0.6, y - y0));
    col[i * 3] = _c.r; col[i * 3 + 1] = _c.g; col[i * 3 + 2] = _c.b;
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
}

// ---------------------------------------------------------------------------
// VEINS — tapered tubes along a smooth random walk on one flat face

export interface VeinSpec {
  shell: ShellSpec;
  axis: number;
  sign: 1 | -1;
  /** start (u,v), heading (radians in the u/v plane), length, start radius */
  u: number; v: number; heading: number; length: number; radius: number;
  seed: number;
  /** heading wobble scale (1 = free wander; small = follows its heading) */
  wander?: number;
}

/** The face's flat (un-rounded) tangent rectangle, inset by `m`. */
export function faceRect(box: MapBox, axis: number, m: number): [number, number, number, number] {
  const ta = axis === 0 ? [1, 2] : axis === 1 ? [0, 2] : [0, 1];
  return [
    box.p[ta[0]] - box.s[ta[0]] / 2 + m, box.p[ta[0]] + box.s[ta[0]] / 2 - m,
    box.p[ta[1]] - box.s[ta[1]] / 2 + m, box.p[ta[1]] + box.s[ta[1]] / 2 - m,
  ];
}

/** Sample the vein's centre-line points (world) + radii. Pure; the bench uses it. */
export function veinPaths(spec: VeinSpec): { pts: THREE.Vector3[]; r: number[] }[] {
  const out: { pts: THREE.Vector3[]; r: number[] }[] = [];
  const rnd = rng(spec.seed);
  const rect = faceRect(spec.shell.box, spec.axis, 0.25);
  const wander = spec.wander ?? 1;
  const plane = spec.shell.box.p[spec.axis] + (spec.sign * spec.shell.box.s[spec.axis]) / 2;
  const walk = (u0: number, v0: number, hd: number, len: number, r0: number, depth: number): void => {
    const step = 0.35;
    const n = Math.max(3, Math.floor(len / step));
    const pts: THREE.Vector3[] = [], rs: number[] = [];
    let u = u0, v = v0, h = hd, turn = 0;
    for (let i = 0; i <= n; i++) {
      if (u < rect[0] || u > rect[1] || v < rect[2] || v > rect[3]) break;
      const t = i / n;
      const r = Math.min(VEIN_R_MAX, r0 * (1 - 0.65 * t));
      const p = surfacePoint(spec.shell, spec.axis, spec.sign, u, v, new THREE.Vector3());
      // sink the tube ~60 % into the skin (a vein under skin, a soft ridge,
      // not a hose lying on it) — deeper where the skin swells near the limit
      const nrm = [0, 0, 0]; nrm[spec.axis] = spec.sign;
      const d = (p.getComponent(spec.axis) - plane) * spec.sign;
      const lim = (spec.axis === 1 && spec.sign > 0 ? TOP_MAX : SIDE_MAX) - r - 0.012;
      const k = Math.min(d - r * 0.2, lim) - d;
      p.x += nrm[0] * k; p.y += nrm[1] * k; p.z += nrm[2] * k;
      pts.push(p); rs.push(r);
      if (wander > 0.5 && depth < 2 && i > 2 && i < n - 2 && rnd() < 0.09) {
        walk(u, v, h + (rnd() < 0.5 ? -1 : 1) * (0.5 + rnd() * 0.6), len * (0.3 + rnd() * 0.25), r * 0.7, depth + 1);
      }
      turn += (rnd() - 0.5) * 0.35 * wander;
      turn *= 0.8;
      h += turn;
      if (wander < 0.5) h += (hd - h) * 0.3; // pulled back onto its course
      u += Math.cos(h) * step; v += Math.sin(h) * step;
    }
    if (pts.length >= 3) out.push({ pts, r: rs });
  };
  walk(spec.u, spec.v, spec.heading, spec.length, spec.radius, 0);
  return out;
}

/** Tube geometry for one path (non-indexed is not required; indexed OK). */
export function veinGeometry(path: { pts: THREE.Vector3[]; r: number[] }, color: THREE.Color, radial: number, uvTile: number): THREE.BufferGeometry {
  const curve = new THREE.CatmullRomCurve3(path.pts);
  const segs = Math.max(4, path.pts.length * 2);
  const g = new THREE.TubeGeometry(curve, segs, 1, radial, false);
  // taper: rings are (radial+1) vertices each, segs+1 rings
  const P = g.getAttribute("position") as THREE.BufferAttribute;
  const ring = radial + 1;
  const ctr = new THREE.Vector3();
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    curve.getPointAt(t, ctr);
    const f = t * (path.r.length - 1);
    const i0 = Math.floor(f), i1 = Math.min(path.r.length - 1, i0 + 1);
    let r = path.r[i0] + (path.r[i1] - path.r[i0]) * (f - i0);
    // pinch the ends shut so the tube dives into the skin
    r *= smooth(0, 0.08, t) * 0.85 + 0.15;
    if (t > 0.9) r *= 1 - smooth(0.9, 1, t) * 0.85;
    for (let k = 0; k < ring; k++) {
      const vi = i * ring + k;
      P.setXYZ(vi, ctr.x + (P.getX(vi) - ctr.x) * r, ctr.y + (P.getY(vi) - ctr.y) * r, ctr.z + (P.getZ(vi) - ctr.z) * r);
    }
  }
  g.computeVertexNormals();
  boxProjectUV(g, uvTile);
  const col = new Float32Array(P.count * 3);
  for (let i = 0; i < P.count; i++) {
    const m = 0.85 + 0.25 * vnoise(P.getX(i) * 0.8, P.getY(i) * 0.8, P.getZ(i) * 0.8, 91);
    col[i * 3] = color.r * m; col[i * 3 + 1] = color.g * m; col[i * 3 + 2] = color.b * m;
  }
  g.setAttribute("color", new THREE.BufferAttribute(col, 3));
  return g;
}
